import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { User, HazardReport, ReportSeverity, UserRole } from "@/types/sorin";
import { CheckCircle, Clock, XCircle, FileText, Trophy, Flame, MessageSquare, ChevronRight, Star, TrendingUp, Shield } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { BadgeDisplay } from "@/components/BadgeDisplay";
import { HazardDiversityTracker } from "@/components/HazardDiversityTracker";
import { GeminiHeader } from "@/components/GeminiHeader";
import { motion } from "framer-motion";
import { format } from "date-fns";

const History = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [reports, setReports] = useState<HazardReport[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [profile, setProfile] = useState<any>(null);

  useEffect(() => {
    checkAuthAndFetchData();
  }, []);

  const checkAuthAndFetchData = async () => {
    try {
      const { data: { user: authUser } } = await supabase.auth.getUser();

      if (!authUser) {
        navigate("/");
        return;
      }

      const { data: profile, error: profileError } = await supabase
        .from("worker_profiles")
        .select("*")
        .eq("user_id", authUser.id)
        .maybeSingle();

      if (profileError || !profile) {
        navigate("/");
        return;
      }

      setProfile(profile);

      const userData: User = {
        id: profile.id,
        displayName: profile.display_name,
        role: profile.role as UserRole,
        team: profile.team,
        xp: profile.total_xp || 0,
        streak: profile.consecutive_safe_days,
        badges: (profile.badges && Array.isArray(profile.badges) ? profile.badges : []) as any[],
        psychometricVector: [],
        baselineRisk: profile.baseline_risk_score,
        currentRisk: profile.current_risk_score,
        anonymousMode: false,
        createdAt: new Date(profile.created_at),
      };

      setUser(userData);
      fetchReports(profile.id);
    } catch (error) {
      console.error("Error checking auth:", error);
      navigate("/");
    }
  };

  const fetchReports = async (userProfileId: string) => {
    try {
      const { data, error } = await supabase
        .from("peer_reports")
        .select("*, verification_status, supervisor_notes, clarification_response")
        .eq("reporter_id", userProfileId)
        .order("created_at", { ascending: false });

      if (error) throw error;

      const mapRiskRatingToSeverity = (riskRating: string): ReportSeverity => {
        switch (riskRating) {
          case "very_low":
          case "low":
            return "low";
          case "medium":
            return "medium";
          case "high":
            return "high";
          case "very_high":
            return "critical";
          default:
            return "medium";
        }
      };

      const transformedReports: (HazardReport & { 
        needsClarification?: boolean;
        supervisorNotes?: string;
        clarificationResponse?: string;
      })[] = (data || []).map((report) => ({
        id: report.id,
        reporterId: report.reporter_id || "",
        location: report.location,
        description: report.description,
        hazardType: report.hazard_category as any,
        severity: mapRiskRatingToSeverity(report.risk_rating),
        status: report.verification_status === "verified" || report.verification_status === "hazard_confirmed" 
          ? "confirmed" 
          : report.verification_status === "rejected" 
            ? "rejected" 
            : "pending",
        timestamp: new Date(report.created_at),
        reporterName: user?.displayName || "Unknown",
        xpAwarded: report.xp_awarded || 0,
        xpConfirmed: !report.xp_pending && (report.verification_status === "verified" || report.verification_status === "hazard_confirmed"),
        confidence: 0.85,
        requiresHumanReview: false,
        requiresPhoto: false,
        askClarify: false,
        needsClarification: report.verification_status === "needs_clarification",
        supervisorNotes: report.supervisor_notes,
        clarificationResponse: report.clarification_response,
      }));

      setReports(transformedReports);
    } catch (error) {
      console.error("Error fetching reports:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const getSeverityBadge = (severity: ReportSeverity) => {
    const config = {
      low: { label: "Rendah", color: "text-emerald-600 dark:text-emerald-400" },
      medium: { label: "Sedang", color: "text-amber-600 dark:text-amber-400" },
      high: { label: "Tinggi", color: "text-rose-600 dark:text-rose-400" },
      critical: { label: "Kritis", color: "text-rose-600 dark:text-rose-400" },
    };
    const { label, color } = config[severity] || config.medium;
    return <span className={`text-xs font-medium ${color}`}>{label}</span>;
  };

  const getStatusIcon = (status: string, needsClarification?: boolean) => {
    if (needsClarification) return <div className="h-2.5 w-2.5 rounded-full bg-amber-500" />;
    if (status === "confirmed") return <div className="h-2.5 w-2.5 rounded-full bg-emerald-500" />;
    if (status === "rejected") return <div className="h-2.5 w-2.5 rounded-full bg-rose-500" />;
    return <div className="h-2.5 w-2.5 rounded-full bg-amber-400" />;
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-background">
      <GeminiHeader title="Laporan Saya" subtitle="Riwayat laporan bahaya" />

      <main className="max-w-2xl mx-auto px-4 py-6">
        {/* Stats Cards */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid grid-cols-3 gap-3 mb-6"
        >
          <div className="rounded-2xl bg-muted/50 dark:bg-muted/30 p-4">
            <div className="flex items-center gap-2 mb-2">
              <FileText className="h-4 w-4 text-muted-foreground" />
            </div>
            <p className="text-2xl font-bold text-foreground">{reports.length}</p>
            <p className="text-xs text-muted-foreground">Laporan</p>
          </div>
          
          <div className="rounded-2xl bg-muted/50 dark:bg-muted/30 p-4">
            <div className="flex items-center gap-2 mb-2">
              <Star className="h-4 w-4 text-muted-foreground" />
            </div>
            <p className="text-2xl font-bold text-foreground">{user.xp}</p>
            <p className="text-xs text-muted-foreground">XP</p>
          </div>
          
          <div className="rounded-2xl bg-muted/50 dark:bg-muted/30 p-4">
            <div className="flex items-center gap-2 mb-2">
              <Flame className="h-4 w-4 text-muted-foreground" />
            </div>
            <p className="text-2xl font-bold text-foreground">{user.streak}</p>
            <p className="text-xs text-muted-foreground">Streak</p>
          </div>
        </motion.div>

        {/* Badges */}
        {profile?.badges && profile.badges.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mb-6 p-4 rounded-2xl bg-muted/40 shadow-sm"
          >
            <div className="flex items-center gap-2 mb-3">
              <Trophy className="h-4 w-4 text-amber-500" />
              <h3 className="text-sm font-medium">Pencapaian</h3>
            </div>
            <BadgeDisplay badges={profile.badges} maxDisplay={8} />
          </motion.div>
        )}

        {/* Hazard Diversity Tracker */}
        {profile && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="mb-6"
          >
            <HazardDiversityTracker workerId={profile.id} />
          </motion.div>
        )}

        {/* Reports List */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div className="flex items-center gap-2 mb-4">
            <div className="w-1 h-5 rounded-full bg-primary" />
            <h3 className="font-semibold">Riwayat Laporan</h3>
          </div>

          {isLoading ? (
            <div className="py-12 text-center">
              <div className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            </div>
          ) : reports.length === 0 ? (
            <div className="py-12 text-center rounded-2xl bg-muted/30">
              <div className="w-14 h-14 rounded-2xl bg-muted/50 flex items-center justify-center mx-auto mb-4">
                <FileText className="h-7 w-7 text-muted-foreground" />
              </div>
              <p className="text-muted-foreground text-sm">Belum ada laporan</p>
            </div>
          ) : (
            <div className="space-y-3">
              {reports.map((report: any, index) => (
                <motion.div
                  key={report.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.05 * index }}
                  onClick={() => navigate(`/report/${report.id}`)}
                  className="p-4 rounded-2xl bg-card/80 hover:bg-card shadow-md shadow-black/5 dark:shadow-black/20 border border-border/50 cursor-pointer group transition-all hover:shadow-lg hover:border-border"
                >
                  <div className="flex items-start gap-3">
                    {/* Status indicator */}
                    <div className="mt-1.5">
                      {getStatusIcon(report.status, report.needsClarification)}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-sm">{report.location}</span>
                        {report.needsClarification && !report.clarificationResponse && (
                          <span className="text-[10px] font-medium text-amber-600 dark:text-amber-400 bg-amber-500/10 px-1.5 py-0.5 rounded">
                            Info
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground line-clamp-1 mb-2">{report.description}</p>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground">
                        <span>{format(new Date(report.timestamp), 'dd MMM yyyy')}</span>
                        <span>•</span>
                        {getSeverityBadge(report.severity)}
                        {report.xpAwarded > 0 && (
                          <>
                            <span>•</span>
                            <span className="font-medium text-amber-600 dark:text-amber-400">+{report.xpAwarded} XP</span>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Arrow */}
                    <ChevronRight className="h-4 w-4 text-muted-foreground/50 mt-2 group-hover:text-foreground transition-colors" />
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>
      </main>
    </div>
  );
};

export default History;